import { useCallback, useState } from 'react';
import { leaveEvent } from '@/utils/leaveEvent';

export interface LeavableEvent {
  id: string;
  name?: string;
}

type ShowToast = (message: string, type?: 'success' | 'error' | 'info') => void;

/**
 * Confirm-then-leave orchestration shared by the event preview modal and the
 * "Currently involved in..." cards, so both surfaces behave and read identically.
 *
 * The caller owns the UI: render a `ConfirmModal` (variant="danger") bound to
 * `pendingEvent` / `confirmLeave` / `cancelLeave`, and its own `Toast` for `showToast`.
 */
export function useLeaveEvent(showToast: ShowToast, onLeft?: (eventId: string) => void) {
  const [pendingEvent, setPendingEvent] = useState<LeavableEvent | null>(null);
  const [leaving, setLeaving] = useState(false);

  const requestLeave = useCallback((event: LeavableEvent) => {
    setPendingEvent(event);
  }, []);

  const cancelLeave = useCallback(() => {
    if (leaving) return;
    setPendingEvent(null);
  }, [leaving]);

  const confirmLeave = useCallback(async () => {
    if (!pendingEvent || leaving) return;
    setLeaving(true);
    try {
      const result = await leaveEvent(pendingEvent.id);
      if (!result.ok) {
        showToast(result.error || 'Failed to leave the event. Please try again.', 'error');
        return;
      }
      if (result.left) {
        showToast("You've left this event.", 'success');
      } else if (result.applicationWithdrawn) {
        showToast('Application withdrawn.', 'success');
      } else {
        showToast("You're no longer part of this event.", 'info');
      }
      setPendingEvent(null);
      onLeft?.(pendingEvent.id);
    } finally {
      setLeaving(false);
    }
  }, [pendingEvent, leaving, showToast, onLeft]);

  return { pendingEvent, leaving, requestLeave, confirmLeave, cancelLeave };
}
